import styled from '@emotion/styled';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import config from 'config';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { translation } from 'utils/translate';
import LawHistoryBox from './LawHistoryBox';
import NewProjectsBox from './NewProjectsBox';
import ParliamentConversationsBox from './ParliamentConversationsBox';
import SearchVisualizationBox from './SearchVisualizationBox';
import manager from './ThemePageManager';
import './ThemePage.css';

// styled-component for passing color prop to the underline
const BoxTitle = styled('h4')(({ color }) => ({
  position: 'relative',
  display: 'block',
  width: 'fit-content',
  margin: '0 auto .5em',
  textAlign: 'center',

  // custom underline
  '&::after': {
    content: '""',
    display: 'block',
    width: '4rem',
    height: '2px',
    backgroundColor: color,
    margin: '0 auto 0',
  },
}));

const ThemePage = ({ dataLang }) => {
  const slug = useParams().tunniste;

  // change the active page and language of the data manager
  manager.changePage(slug, dataLang);

  if (!manager.pageData) return <h1>404</h1>;

  return (
    <>
      <Helmet>
        <title>
          {manager.pageData.title} | {config.site.name}
        </title>
      </Helmet>
      <Grid className="center-content" container gap={4.5}>
        <TitleBar pageData={manager.pageData} />
        <DescriptionBar
          pageData={manager.pageData}
          lang={dataLang}
        />
        <SearchBar pageData={manager.pageData} />
        <Grid
          className="center-content"
          // horizontal margin for md and up
          xs={10}
          sx={{ mx: { md: '1em' } }}
          container
          item
          gap={4.5}
        >
          <ContentBox
            style={{
              border: '5px solid',
              borderColor: manager.pageData.lightColor,
            }}
            title={'Uudet hankkeet'}
          >
            <NewProjectsBox pageData={manager.pageData} />
          </ContentBox>
          <ContentBox
            title={'Keskeisten säädösten muutoshistoria'}
            style={{
              border: '5px solid',
              borderColor: manager.pageData.darkColor,
            }}
          >
            <LawHistoryBox pageData={manager.pageData} />
          </ContentBox>
          <ContentBox
            title="Asiakirjoja vuosittain"
            style={{
              border: '5px solid',
              borderColor: manager.pageData.darkColor,
            }}
          >
            <SearchVisualizationBox
              pageData={manager.pageData}
            />
          </ContentBox>
          <ContentBox
            title="Eduskunnassa keskusteltua"
            style={{
              border: '5px solid',
              borderColor: manager.pageData.lightColor,
              backgroundColor: manager.pageData.lightColor,
            }}
          >
            <ParliamentConversationsBox
              pageData={manager.pageData}
            />
          </ContentBox>
        </Grid>
        <LearningBar
          pageData={manager.pageData}
          lang={dataLang}
        />
      </Grid>
    </>
  );
};

const ContentBox = ({ title, style, children }) => {
  // wrapper for the boxes. Injects an isLoading state
  // to the boxes and renders a spinning circle while true
  const [isLoading, setIsLoading] = useState(true);

  const childrenWithCallback = React.Children.map(
    children,
    (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, { setIsLoading });
      }
      return child;
    },
  );

  // extract pageData prop from children since it is not
  // passed to ContentBox. TODO: it maybe could be though
  const { pageData } = childrenWithCallback[0]?.props;

  // default title underline is yellow.
  // if bg is yellow, use black underline
  const isBgYellow =
    style.backgroundColor === pageData.lightColor;

  return (
    <Grid
      item
      lg={5}
      md={5}
      sm={12}
      xs={12}
      className="theme-box"
      style={style}
    >
      <BoxTitle
        color={isBgYellow ? 'black' : pageData.lightColor}
      >
        {title}
      </BoxTitle>
      {isLoading && (
        <CircularProgress
          className="loading-spinner"
          size={68}
        />
      )}
      {childrenWithCallback}
    </Grid>
  );
};

const TitleBar = ({ pageData }) => {
  const lang = useSelector((store) => store.lang);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // figure out whether to display disclaimer about
  // language version availability
  const showDisclaimer = lang === 'se';

  return (
    <Grid
      xs={8}
      className="title-row"
      container
      item
      direction={isMdUp ? 'row' : 'column'}
      sx={{ marginTop: { xs: '1em', md: 0 } }}
    >
      <Grid item>
        {pageData?.logo && (
          <img
            alt="SILE-logo"
            src={pageData.logo}
            style={{ maxWidth: isMdUp ? 180 : 100 }}
          />
        )}
      </Grid>
      <Grid item xs={6}>
        <p
          className="title-text"
          style={{ margin: !isMdUp && '0.5em' }}
        >
          {pageData.title}
          {showDisclaimer && (
            <span>{' (endast på finska)'}</span>
          )}
        </p>
      </Grid>
      <Grid item>
        <img
          alt="SILE-megaphone"
          src={pageData.descriptionImage}
          style={{ maxWidth: isMdUp ? 130 : 80 }}
        />
      </Grid>
    </Grid>
  );
};

const DescriptionBar = ({ pageData, lang }) => {
  const [isOpen, setIsOpen] = useState(false);

  const parseMarkdown = (text) => {
    /*
    Parses a string to markdown by converting markdown syntax
    into HTML tags. Outputs html string.
    Suppports **bold**, *italic*, and three levels of headings (h1-h3).
    Supports ONE (1) list. Additionally replaces newlines \n with <br /> tag.
    source:
    https://randyperkins2k.medium.com/writing-a-simple-markdown-parser-using-javascript-1f2e9449a558
    */
    let toHTML = text
      .replace(/^- (.*$)/gim, '<li>$1</li>') // li tag
      .replace(/^### (.*$)/gim, '<h3>$1</h3>') // h3 tag
      .replace(/^## (.*$)/gim, '<h2>$1</h2>') // h2 tag
      .replace(/^# (.*$)/gim, '<h1>$1</h1>') // h1 tag
      .replace(/\*\*(.*)\*\*/gim, '<b>$1</b>') // bold text
      .replace(/\*(.*)\*/gim, '<i>$1</i>') // italic text
      .replace(/(?<!<\/li>)\n/gim, '<br/>'); // newline to br

    // insert <ul> tags around li elements
    if (toHTML.includes('<li>')) {
      // insert <ul> before the first <li>
      const firstLiIndex = toHTML.indexOf('<li>');
      toHTML =
        toHTML.slice(0, firstLiIndex) +
        '<ul>' +
        toHTML.slice(firstLiIndex);
      // insert </ul> after the last <li>
      // need to get the index here because text length changes
      const lastLiIndex = toHTML.lastIndexOf('</li>') + 5;
      toHTML =
        toHTML.slice(0, lastLiIndex) +
        '</ul>' +
        toHTML.slice(lastLiIndex);
    }
    return toHTML.trim();
  };

  const getParsedDescription = (text, typeClass) => {
    return (
      <p
        className={'description-text ' + typeClass}
        // eslint-disable-next-line
        dangerouslySetInnerHTML={{
          __html: parseMarkdown(text),
        }}
      ></p>
    );
  };

  return (
    <Grid xs={10} container item>
      <Grid item xs={12}>
        {pageData?.description &&
          getParsedDescription(
            pageData?.description,
            'intro',
          )}
        {pageData?.descriptionShort &&
          pageData?.descriptionRest && (
            <>
              {getParsedDescription(
                pageData?.descriptionShort,
                'intro',
              )}
              {isOpen &&
                getParsedDescription(
                  pageData?.descriptionRest,
                  'full',
                )}
              <button
                className="description-show-more"
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen
                  ? translation(
                      'themepages',
                      'description_show_less',
                      lang,
                    )
                  : translation(
                      'themepages',
                      'description_show_more',
                      lang,
                    )}
              </button>
            </>
          )}
      </Grid>
    </Grid>
  );
};

const SearchBar = ({ pageData }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const handleTermChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const params = new URLSearchParams({
      term: searchTerm,
      prefilters: JSON.stringify({
        asiasanat: pageData.sharedIgnore
          .concat(pageData.keywords)
          .map((word) => word.replaceAll('NOT', 'EI')),
      }),
    });
    const url = '/?' + params;
    window.location.href = url;
  };

  return (
    <form
      className="center-content"
      style={{
        display: 'flex',
        width: '100%',
        backgroundColor: pageData.darkColor,
        padding: '1.5em 0',
      }}
      onSubmit={handleSearchSubmit}
    >
      <Grid item xs={9} sm={6} lg={4}>
        <TextField
          className="search-term"
          variant="outlined"
          placeholder={pageData.searchPlaceholder}
          value={searchTerm}
          onChange={handleTermChange}
        />
      </Grid>
      <Button
        className="search-submit"
        aria-label="hae"
        variant="outlined"
        type="submit"
        disableTouchRipple
        sx={{
          backgroundColor: pageData.lightColor,
          ':hover': {
            backgroundColor: pageData.lightColor,
          },
        }}
      >
        <SearchIcon />
      </Button>
    </form>
  );
};

const LearningBar = ({ pageData, lang }) => {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  return (
    <Grid
      container
      item
      xs={10}
      sx={{
        backgroundColor: pageData.lightColor,
        justifyContent: 'space-around',
        alignItems: 'center',
        padding: '1.5em 0',
      }}
      direction={{ xs: 'column', sm: 'row' }}
    >
      {/* when below sm threshold,
      move img above the title in the same element*/}
      {isSmUp && (
        <Grid item xs={1}>
          <img alt="logo" src="/opi_icon.svg" />
        </Grid>
      )}
      <Grid
        item
        container
        xs={2}
        gap={0.5}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {!isSmUp && <img alt="logo" src="/opi_icon.svg" />}
        <p style={{ fontWeight: 'bold' }}>
          {translation('opi_miten_lait_syntyvät', lang)}
        </p>
      </Grid>
      <Grid item xs={7}>
        <Grid
          container
          item
          columns={{ xs: 1, sm: 2 }}
          style={{ padding: '0 1em' }}
        >
          <LearningLink
            link="https://www.youtube.com/watch?v=KdIMfFCV2Mw/"
            translateId="video_näin_lait_synytvät"
            lang={lang}
          />
          {lang === 'se' && (
            <LearningLink
              link="https://www.youtube.com/watch?v=2jU6QArc5LY"
              translateId="video_näin_lait_syntyvät_en"
              lang={lang}
            />
          )}
          <LearningLink
            link="https://www.youtube.com/watch?v=5oFocGzFHhs"
            translateId="video_kuuleminen_lainvalmistelussa"
            lang={lang}
          />
          {lang === 'se' && (
            <LearningLink
              link="https://www.youtube.com/watch?v=CrtleyvQcjw"
              translateId="video_kuuleminen_lainvalmistelussa_en"
              lang={lang}
            />
          )}
          <LearningLink
            link="https://digicampus.fi/course/view.php?id=4775"
            translateId="mooc"
            lang={lang}
          />
          <LearningLink
            link="/dictionary"
            translateId="lainvalmistelun_sanakirja"
            lang={lang}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const LearningLink = ({ link, translateId, lang }) => {
  const isHTTPS = link.includes('https://');
  const style = {
    color: 'black',
    fontWeight: '700',
    textDecoration: 'underline',
    textDecorationThickness: 1,
    marginLeft: '10px',
    marginTop: '5px',
  };

  return (
    <Grid item xs={1}>
      <img alt="arrow" src="/hero_arrow_right.svg" />
      {isHTTPS ? (
        <a
          style={style}
          href={link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {translation(translateId, lang)}
        </a>
      ) : (
        // relative route path
        <Link style={style} to={link}>
          {translation(translateId, lang)}
        </Link>
      )}
    </Grid>
  );
};

export default ThemePage;
